<script setup lang="ts">
import { NTag } from "naive-ui";
import { computed, ref } from "vue";
import { mediaExtensions } from "../utils/constants";
import { toHHMM } from "../utils/dates";
import { useTeam } from "../hooks/useTeam";
import { useOrganization } from "../hooks/useOrganization";
import AttachmentProxy from "./AttachmentProxy.vue";
import Avatar from "./Avatar.vue";
import ConfirmationModal from "./ConfirmationModal.vue";
import MarkdownText from "./MarkdownText.vue";
import RichTextEditor from "./RichTextEditor.vue";
import ProperSelect from "./proper-inputs/proper-select.vue";
import { getUserEmail } from "../config/firebase.ts";

export interface CommentData {
    id?: string;
    text?: string;
    content?: string;
    author: string;
    createdAt?: string | Date | null;
    attachments?:
        | Array<
              | {
                    id: string;
                    name: string;
                    url: string;
                }
              | {
                    id: string;
                    file: { name: string; url: string };
                }
          >
        | string;
}

export interface MentionOption {
    label: string;
    value: string;
}

const props = withDefaults(
    defineProps<{
        comment: CommentData;
        canEdit?: boolean;
        canDelete?: boolean;
        isEditing?: boolean;
        isUploading?: boolean;
        isDeleting?: boolean;
        mentionOptions?: MentionOption[];
        tag?: {
            type: "default" | "primary" | "info" | "success" | "warning" | "error";
            text?: string;
            class?: string;
        } | null;
        attachmentComponent?: string;
    }>(),
    {
        canEdit: false,
        canDelete: false,
        isEditing: false,
        isUploading: false,
        isDeleting: false,
        attachmentComponent: "attachment-proxy",
        mentionOptions: () => [],
        tag: null,
    },
);

const emit = defineEmits<{
    edit: [];
    cancel: [];
    save: [text: string, files: File[]];
    delete: [];
    click: [event: MouseEvent];
}>();

const localText = ref("");
const newFiles = ref<File[]>([]);
const showDeleteModal = ref(false);

const { employeeMailToName } = useTeam();
const { contactEmailToName, userNameFromContacts } = useOrganization();

const commentText = computed(() => {
    return props.comment.text || props.comment.content || "";
});

const isCurrentUserAuthor = computed(() => {
    return props.comment.author === getUserEmail();
});

const displayName = computed(() => {
    if (isCurrentUserAuthor.value && userNameFromContacts.value) {
        return userNameFromContacts.value;
    }
    const employeeName = employeeMailToName(props.comment.author);
    if (employeeName !== props.comment.author) {
        return employeeName;
    }
    return contactEmailToName(props.comment.author);
});

const avatarName = computed(() => {
    if (isCurrentUserAuthor.value && userNameFromContacts.value) {
        return userNameFromContacts.value;
    }
    return props.comment.author;
});

const canUserEdit = computed(() => {
    return props.canEdit && isCurrentUserAuthor.value;
});

const canUserDelete = computed(() => {
    return props.canDelete && isCurrentUserAuthor.value;
});

const imageAttachments = computed(() => {
    if (!props.comment.attachments) return [];

    if (typeof props.comment.attachments === "string") {
        const extension = props.comment.attachments.split(".").pop()?.toLowerCase() || "";
        return mediaExtensions.includes(extension) ? [props.comment.attachments] : [];
    }

    return props.comment.attachments.filter((attachment) => {
        const name = "name" in attachment ? attachment.name : attachment.file.name;
        return mediaExtensions.includes(name.split(".").pop()?.toLowerCase() || "");
    });
});

const nonImageAttachments = computed(() => {
    if (!props.comment.attachments) return [];

    if (typeof props.comment.attachments === "string") {
        const extension = props.comment.attachments.split(".").pop()?.toLowerCase() || "";
        return !mediaExtensions.includes(extension) ? [props.comment.attachments] : [];
    }

    return props.comment.attachments.filter((attachment) => {
        const name = "name" in attachment ? attachment.name : attachment.file.name;
        return !mediaExtensions.includes(name.split(".").pop()?.toLowerCase() || "");
    });
});

function handleEdit() {
    if (!canUserEdit.value || props.isEditing) return;
    localText.value = commentText.value;
    newFiles.value = [];
    emit("edit");
}

function onCancel() {
    localText.value = "";
    newFiles.value = [];
    emit("cancel");
}

function onSave() {
    emit("save", localText.value, newFiles.value);
}

const testValue = ref("");
</script>

<template>
    <div class="p-4 rounded-lg group">
        <div class="flex justify-between mb-3">
            <div class="flex items-center gap-4">
                <avatar :name="avatarName ?? ''" />
                <div class="flex items-center gap-2 cursor-default">
                    <span class="font-bold">{{ displayName }}</span>
                    <button
                        v-if="canUserEdit && !isEditing"
                        class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-gray-100 rounded"
                        @click.stop="handleEdit"
                        title="Edit comment"
                    >
                        <span class="material-symbols-outlined text-sm text-gray-500">edit</span>
                    </button>
                </div>
                <n-tag
                    v-if="tag"
                    :type="tag?.type"
                    size="small"
                    :bordered="false"
                    class="py-2 w-fit"
                >
                    <span :class="tag?.class">
                        {{ tag?.text }}
                    </span>
                </n-tag>
            </div>
            <div class="text-label text-xs">
                <span>{{ toHHMM(new Date(comment.createdAt ?? new Date())) }}</span>
            </div>
        </div>

        <markdown-text v-if="!isEditing" :content="commentText" class="py-1 px-1 mb-2" />

        <div v-if="isEditing">
            <rich-text-editor
                btn-text="Save"
                class="mb-4 w-full"
                :model-value="localText"
                placeholder="Type here..."
                :file-list="newFiles"
                :show-submit-button="false"
                :submit-disabled="isDeleting || isUploading"
                :mention-options="mentionOptions"
                :enable-mentions="false"
                @update:model-value="(value: string) => (localText = value)"
                @update:file-list="(files: File[] | undefined) => (newFiles = files || [])"
                @submit="onSave"
            />
        </div>

        <div v-if="imageAttachments.length > 0">
            <div>
                <attachment-proxy
                    v-for="attachment in imageAttachments"
                    :id="typeof attachment === 'string' ? attachment : attachment.id"
                    :key="typeof attachment === 'string' ? attachment : attachment.id"
                    :name="
                        typeof attachment === 'string'
                            ? attachment
                            : 'name' in attachment
                              ? attachment.name
                              : attachment.file.name
                    "
                    :url="
                        typeof attachment === 'string'
                            ? ''
                            : 'url' in attachment
                              ? attachment.url
                              : attachment.file.url
                    "
                    :hide-delete="true"
                    class="flex-shrink-0 mr-2 last:mr-0 transition-color-opacity duration-350 font-mono uppercase text-[10px] px-2 py-1 flex text-black rounded items-center center mb-1 relative overflow-hidden cursor-pointer"
                    @click.stop
                />
            </div>
        </div>

        <ul v-if="nonImageAttachments.length > 0">
            <li
                v-for="attachment in nonImageAttachments"
                :key="typeof attachment === 'string' ? attachment : attachment.id"
            >
                <attachment-proxy
                    :id="typeof attachment === 'string' ? attachment : attachment.id"
                    :name="
                        typeof attachment === 'string'
                            ? attachment
                            : 'name' in attachment
                              ? attachment.name
                              : attachment.file.name
                    "
                    :url="
                        typeof attachment === 'string'
                            ? ''
                            : 'url' in attachment
                              ? attachment.url
                              : attachment.file.url
                    "
                    class="transition-color-opacity duration-350 font-mono uppercase text-[10px] px-2 py-1 flex text-black rounded items-center center mb-1 relative overflow-hidden cursor-pointer"
                    :hide-delete="true"
                    @click.stop
                />
            </li>
        </ul>

        <div v-if="isEditing" class="flex justify-end w-full gap-2 my-3 mt-2">
            <proper-select :model-value="testValue" :options="[]" />
            <button
                class="secondary-button"
                :disabled="isDeleting || isUploading"
                @click.stop="onCancel"
            >
                <span class="text-label">Cancel</span>
            </button>
            <button
                v-if="canUserDelete"
                class="secondary-button text-red-400"
                :disabled="isDeleting || isUploading"
                @click.stop="showDeleteModal = true"
            >
                <span class="material-symbols-outlined mr-1">delete</span>
                Delete
            </button>
            <button class="button" :disabled="isDeleting || isUploading" @click.stop="onSave">
                Save
            </button>
        </div>
    </div>

    <confirmation-modal
        v-if="showDeleteModal"
        danger
        title="Confirm Delete Comment"
        content="Are you sure you want to delete this comment?"
        negative-text="Cancel"
        positive-text="Delete"
        @positive-click="
            emit('delete');
            showDeleteModal = false;
        "
        @negative-click="showDeleteModal = false"
    />
</template>
